import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
    User,
    Mail,
    Phone,
    Shield,
    Bell,
    Save,
    AlertCircle,
    Loader2,
    Lock,
    Eye,
    EyeOff,
    Check,
    Calendar,
    Briefcase,
    Camera,
    Upload,
    Sun,
    Moon,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const EmployeeSettingsPage = () => {
    const { user, refreshUser } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const fileInputRef = useRef(null);

    // Profile State
    const [profile, setProfile] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        start_date: '',
        profile_picture_url: '',
    });

    // Password State
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});

    // Notification Settings State
    const [notificationSettings, setNotificationSettings] = useState({
        email_task_assigned: true,
        email_task_reminder: true,
        email_notice_posted: true,
        email_course_assigned: false,
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await api.get('/employees/me');
            const employee = response.data.employee;

            setProfile({
                first_name: employee.first_name || '',
                last_name: employee.last_name || '',
                email: employee.email || '',
                phone: employee.phone || '',
                position: employee.position || '',
                start_date: employee.start_date ? employee.start_date.split('T')[0] : '',
                profile_picture_url: employee.profile_picture_url || '',
            });

            if (employee.notification_settings) {
                setNotificationSettings((prev) => ({
                    ...prev,
                    ...employee.notification_settings,
                }));
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post('/employees/me/profile-picture', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setProfile((prev) => ({
                ...prev,
                profile_picture_url: response.data.profile_picture_url,
            }));
            toast.success('Profile picture updated!');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to upload photo');
        } finally {
            setUploadingPhoto(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            await api.put('/employees/me', {
                first_name: profile.first_name,
                last_name: profile.last_name,
                phone: profile.phone,
            });
            toast.success('Profile updated successfully!');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const validatePassword = () => {
        const errors = {};
        if (!passwordData.current_password) errors.current_password = 'Current password is required';
        if (!passwordData.new_password) errors.new_password = 'New password is required';
        else if (passwordData.new_password.length < 8) errors.new_password = 'Password must be at least 8 characters';
        if (passwordData.new_password !== passwordData.confirm_password) errors.confirm_password = 'Passwords do not match';
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChangePassword = async () => {
        if (!validatePassword()) return;
        setSaving(true);
        try {
            await api.put('/employees/me/password', {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password,
            });
            toast.success('Password changed successfully!');
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            setPasswordErrors({});
        } catch (error) {
            if (error.response?.data?.error === 'Current password is incorrect') {
                setPasswordErrors({ current_password: 'Current password is incorrect' });
            } else {
                toast.error(error.response?.data?.error || 'Failed to change password');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSaveNotifications = async () => {
        setSaving(true);
        try {
            await api.put('/employees/me/notifications', notificationSettings);
            toast.success('Notification preferences updated!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update preferences');
        } finally {
            setSaving(false);
        }
    };

    // ─── Reusable Toggle Component ──────────────────
    const Toggle = ({ enabled, onChange, label, description, badge }) => (
        <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-medium text-foreground">{label}</p>
                    {badge && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-info/10 text-info rounded">
                            {badge}
                        </span>
                    )}
                </div>
                {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
            <button
                onClick={onChange}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${enabled ? 'bg-accent' : 'bg-muted'}`}
            >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${enabled ? 'left-7' : 'left-1'}`} />
            </button>
        </div>
    );

    const tabs = [
        { id: 'profile', label: 'My Profile', icon: User },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin no-theme-transition rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
            </div>

            {/* Tabs */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="border-b border-border">
                    <nav className="flex overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                                    ? 'border-accent text-accent'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                    }`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* ─── Profile Tab ────────────────────────────── */}
                    {activeTab === 'profile' && (
                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-4">Personal Information</h3>

                                {/* Avatar with Upload */}
                                <div className="flex items-center gap-6 mb-6">
                                    <div className="relative group">
                                        {profile.profile_picture_url ? (
                                            <img
                                                src={profile.profile_picture_url}
                                                alt="Profile"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-card shadow-lg"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 bg-accent rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                                                {profile.first_name?.[0]}{profile.last_name?.[0]}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingPhoto}
                                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                        >
                                            {uploadingPhoto ? (
                                                <Loader2 size={24} className="text-white animate-spin no-theme-transition" />
                                            ) : (
                                                <Camera size={24} className="text-white" />
                                            )}
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoUpload}
                                            className="hidden"
                                        />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-foreground text-lg">{profile.first_name} {profile.last_name}</p>
                                        <p className="text-muted-foreground">{profile.position || 'Employee'}</p>
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingPhoto}
                                            className="mt-2 text-sm text-accent hover:text-accent/80 flex items-center gap-1"
                                        >
                                            <Upload size={14} />
                                            {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">First Name</label>
                                        <input
                                            type="text"
                                            value={profile.first_name}
                                            onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                            placeholder="John"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">Last Name</label>
                                        <input
                                            type="text"
                                            value={profile.last_name}
                                            onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                            placeholder="Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Email Address
                                            <span className="text-xs text-muted-foreground font-normal ml-2">(Cannot be changed)</span>
                                        </label>
                                        <div className="relative">
                                            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="email"
                                                value={profile.email}
                                                disabled
                                                className="w-full pl-10 pr-4 py-3 bg-secondary border border-border rounded-xl text-muted-foreground cursor-not-allowed"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">Phone Number</label>
                                        <div className="relative">
                                            <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="tel"
                                                value={profile.phone}
                                                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="+1 (555) 000-0000"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Read-only Employment Info */}
                                <div className="mt-6 p-4 bg-secondary rounded-xl">
                                    <p className="font-medium text-foreground mb-3">Employment Info</p>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Briefcase size={16} />
                                            <span>Position: <strong className="text-foreground">{profile.position || 'N/A'}</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Calendar size={16} />
                                            <span>Start Date: <strong className="text-foreground">{profile.start_date ? new Date(profile.start_date).toLocaleDateString() : 'N/A'}</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <hr className="border-border" />

                            {/* Display Preferences Section */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Display Preferences</h3>
                                <p className="text-sm text-muted-foreground mb-6">Customize how Envion looks for you.</p>

                                <div className="max-w-lg">
                                    <label className="block text-sm font-medium text-foreground mb-3">Theme</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Light Mode Card */}
                                        <button
                                            onClick={() => { if (theme === 'dark') toggleTheme(); }}
                                            className={`relative p-4 rounded-xl border-2 transition-all text-left ${theme === 'light'
                                                ? 'border-accent bg-accent/5 shadow-sm'
                                                : 'border-border hover:border-muted-foreground/30 bg-card'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'light' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground'}`}>
                                                    <Sun size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground text-sm">Light</p>
                                                    <p className="text-xs text-muted-foreground">Classic bright interface</p>
                                                </div>
                                            </div>
                                            {/* Theme Preview */}
                                            <div className="rounded-lg overflow-hidden border border-border">
                                                <div className="h-3 bg-[#faf7f3]"></div>
                                                <div className="flex h-12">
                                                    <div className="w-6 bg-[#2a3f54]"></div>
                                                    <div className="flex-1 bg-[#faf7f3] p-1.5 space-y-1">
                                                        <div className="h-1.5 w-8 bg-[#e5e1dd] rounded"></div>
                                                        <div className="h-1.5 w-12 bg-[#e5e1dd] rounded"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            {theme === 'light' && (
                                                <div className="absolute top-3 right-3 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>

                                        {/* Dark Mode Card */}
                                        <button
                                            onClick={() => { if (theme === 'light') toggleTheme(); }}
                                            className={`relative p-4 rounded-xl border-2 transition-all text-left ${theme === 'dark'
                                                ? 'border-accent bg-accent/5 shadow-sm'
                                                : 'border-border hover:border-muted-foreground/30 bg-card'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground'}`}>
                                                    <Moon size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground text-sm">Dark</p>
                                                    <p className="text-xs text-muted-foreground">Easy on the eyes</p>
                                                </div>
                                            </div>
                                            {/* Theme Preview */}
                                            <div className="rounded-lg overflow-hidden border border-border">
                                                <div className="h-3 bg-[#1a2332]"></div>
                                                <div className="flex h-12">
                                                    <div className="w-6 bg-[#2a3f54]"></div>
                                                    <div className="flex-1 bg-[#1a2332] p-1.5 space-y-1">
                                                        <div className="h-1.5 w-8 bg-[#334155] rounded"></div>
                                                        <div className="h-1.5 w-12 bg-[#334155] rounded"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            {theme === 'dark' && (
                                                <div className="absolute top-3 right-3 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className="px-6 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Save size={20} />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ─── Security Tab ───────────────────────────── */}
                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Change Password</h3>
                                <p className="text-sm text-muted-foreground mb-6">Update your password to keep your account secure.</p>

                                <div className="space-y-4 max-w-md">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">Current Password</label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={passwordData.current_password}
                                                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                                                className={`w-full pl-10 pr-12 py-3 bg-card border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground ${passwordErrors.current_password ? 'border-destructive' : 'border-border'}`}
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {passwordErrors.current_password && (
                                            <p className="mt-1.5 text-sm text-destructive flex items-center gap-1">
                                                <AlertCircle size={14} />
                                                {passwordErrors.current_password}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">New Password</label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={passwordData.new_password}
                                                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                                className={`w-full pl-10 pr-12 py-3 bg-card border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground ${passwordErrors.new_password ? 'border-destructive' : 'border-border'}`}
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {passwordErrors.new_password && (
                                            <p className="mt-1.5 text-sm text-destructive flex items-center gap-1">
                                                <AlertCircle size={14} />
                                                {passwordErrors.new_password}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">Confirm New Password</label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={passwordData.confirm_password}
                                                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                                className={`w-full pl-10 pr-12 py-3 bg-card border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground ${passwordErrors.confirm_password ? 'border-destructive' : 'border-border'}`}
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {passwordErrors.confirm_password && (
                                            <p className="mt-1.5 text-sm text-destructive flex items-center gap-1">
                                                <AlertCircle size={14} />
                                                {passwordErrors.confirm_password}
                                            </p>
                                        )}
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            onClick={handleChangePassword}
                                            disabled={saving}
                                            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Shield size={20} />}
                                            Update Password
                                        </button>
                                    </div>
                                </div>

                                {/* Password Tips */}
                                <div className="mt-6 p-4 bg-info/5 border border-info/20 rounded-xl max-w-md">
                                    <p className="font-medium text-info mb-2">Password Tips</p>
                                    <ul className="text-sm text-info/80 space-y-1">
                                        <li className="flex items-center gap-2">
                                            <Check size={14} />
                                            Use at least 8 characters
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <Check size={14} />
                                            Include uppercase and lowercase letters
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <Check size={14} />
                                            Add numbers and special characters
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ─── Notifications Tab ──────────────────────── */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Email Notifications</h3>
                                <p className="text-sm text-muted-foreground mb-6">Choose which notifications you'd like to receive.</p>

                                <div className="space-y-3">
                                    {[
                                        { key: 'email_task_assigned', label: 'Task Assigned', description: 'When a new task is assigned to you', badge: 'Recommended' },
                                        { key: 'email_task_reminder', label: 'Task Reminders', description: 'Reminder emails for upcoming task deadlines' },
                                        { key: 'email_notice_posted', label: 'New Notices', description: 'When a new notice is posted' },
                                        { key: 'email_course_assigned', label: 'Course Assignments', description: 'When a new training course is assigned' },
                                    ].map((item) => (
                                        <Toggle
                                            key={item.key}
                                            enabled={notificationSettings[item.key]}
                                            onChange={() => setNotificationSettings({ ...notificationSettings, [item.key]: !notificationSettings[item.key] })}
                                            label={item.label}
                                            description={item.description}
                                            badge={item.badge}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={handleSaveNotifications}
                                    disabled={saving}
                                    className="px-6 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Save size={20} />}
                                    Save Preferences
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeSettingsPage;
